import React, { useEffect, useRef, useState } from 'react';
import { voiceNoteStore } from '../../services/chat/voiceNoteStore';
import { PlayIcon, StopIcon } from './icons/ChatIcons';

interface VoiceNotePlayerProps {
  messageId: string;
}

/**
 * Escuchar una nota de voz guardada en este dispositivo (KAN-225), para que el
 * productor confirme que se grabó bien. El audio sale de ChatAudioDB.
 */
const VoiceNotePlayer: React.FC<VoiceNotePlayerProps> = ({ messageId }) => {
  const [playing, setPlaying] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  const release = () => {
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  };

  useEffect(() => release, []);

  const fail = () => {
    release();
    setPlaying(false);
    setUnavailable(true);
  };

  const toggle = async () => {
    if (playing) {
      release();
      setPlaying(false);
      return;
    }
    const stored = await voiceNoteStore.get(messageId).catch(() => null);
    if (!stored) {
      fail();
      return;
    }
    const url = URL.createObjectURL(stored.blob);
    urlRef.current = url;
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => {
      release();
      setPlaying(false);
    };
    audio.onerror = fail;
    setPlaying(true);
    try {
      await audio.play();
    } catch {
      fail();
    }
  };

  if (unavailable) {
    return (
      <span className="text-xs text-paper/70" title="El audio ya no está guardado en este dispositivo">
        Audio no disponible
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => { void toggle(); }}
      className="p-1.5 rounded-md bg-paper/15 text-paper hover:bg-paper/25 transition-colors"
      aria-label={playing ? 'Detener audio de la nota' : 'Escuchar nota de voz'}
      title={playing ? 'Detener' : 'Escuchar lo que grabaste'}
    >
      {playing ? <StopIcon className="h-3.5 w-3.5" /> : <PlayIcon className="h-3.5 w-3.5" />}
    </button>
  );
};

export default VoiceNotePlayer;
